/**
 * GOLDEN STANDARD: Pure Presentation Component
 *
 * This component is PURE PRESENTATION.
 * - Reads state from Redux (gameSessionSlice)
 * - Renders UI based on state
 * - NO business logic
 * - NO state machine transitions (middleware handles that)
 * - NO useEffect state management
 */
import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import type { User } from '@/shared/types';
import { Chess } from "chess.js";
import { Chessboard as DummyChessboard } from "react-chessboard";
import ChessGame from "../game/ChessGame";
import ResultModal from "../modals/ResultModal";
import '@/css/inGame.css';
import ShellSideContainer from "@/components/shell/ShellSideContainer";
import { GameState } from "@/shared/constants/game";

// Redux imports
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import {
  selectCurrentGame,
  selectByeMessage,
  selectCancelledTournament,
  selectTournamentId,
  selectIsInLobby,
  selectIsWaitingForRound,
  selectIsByeRound,
  selectIsTournamentOver,
  selectIsTournamentCancelled,
  selectShouldShowGameBoard,
  selectGameOverData,
  selectIsGameOver,
  selectTournamentOverData,
  tournamentJoined,
  gameStarting,
  clearByeMessage,
  clearGameOverData
} from '@/store/slices/gameSessionSlice';

function ChessSessionRedux({ user, gameState }: { user: User | null, gameState: GameState }) {
    const location = useLocation();
    const { sessionID, gameID } = useParams<{ sessionID?: string; gameID?: string }>();
    const dispatch = useAppDispatch();
    const { ...tournamentData } = location.state || {};

    // Read state from gameSessionSlice (single source of truth)
    const gameData = useAppSelector(selectCurrentGame);
    const byeMessage = useAppSelector(selectByeMessage);
    const cancelledTournament = useAppSelector(selectCancelledTournament);
    const stateTournamentId = useAppSelector(selectTournamentId);

    // State machine selectors
    const isInLobby = useAppSelector(selectIsInLobby);
    const isWaitingForRound = useAppSelector(selectIsWaitingForRound);
    const isByeRound = useAppSelector(selectIsByeRound);
    const isTournamentOver = useAppSelector(selectIsTournamentOver);
    const isTournamentCancelled = useAppSelector(selectIsTournamentCancelled);
    const shouldShowGameBoard = useAppSelector(selectShouldShowGameBoard);
    const gameOverData = useAppSelector(selectGameOverData);
    const isGameOver = useAppSelector(selectIsGameOver);
    const tournamentOverData = useAppSelector(selectTournamentOverData);

    // UI state (not business logic)
    const [whiteTime, setWhiteTime] = useState<number | null>(null);
    const [blackTime, setBlackTime] = useState<number | null>(null);
    const [isMuted, setIsMuted] = useState(false);
    const [isRehydratingRouteGame, setIsRehydratingRouteGame] = useState(false);

    const game = new Chess();
    const squareStyles = {};

    // Custom chess pieces using Lichess's staunty set (3D style similar to chess.com Neo)
    const PIECE_SET = 'staunty';
    const pieceComponents: { [piece: string]: ({ squareWidth }: { squareWidth: number }) => React.ReactElement } = {};
    const pieceTypes = ['P', 'N', 'B', 'R', 'Q', 'K'];
    const colors = ['w', 'b'];

    colors.forEach(color => {
        pieceTypes.forEach(type => {
            const piece = `${color}${type}`;
            pieceComponents[piece] = ({ squareWidth }: { squareWidth: number }) => (
                <img
                    src={`https://lichess1.org/assets/piece/${PIECE_SET}/${piece}.svg`}
                    alt={piece}
                    style={{ width: squareWidth, height: squareWidth }}
                />
            );
        });
    });

    // Wood texture styles for chess board (lobby/waiting state)
    const woodLightSquareStyle = {
        background: `
            linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.03) 50%, transparent 100%),
            linear-gradient(90deg,
                rgba(139,119,101,0.1) 0%,
                transparent 20%,
                rgba(139,119,101,0.08) 40%,
                transparent 60%,
                rgba(139,119,101,0.1) 80%,
                transparent 100%
            ),
            linear-gradient(180deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(0,0,0,0.05) 100%),
            #e8d0aa
        `,
        backgroundSize: '100% 100%, 8px 100%, 100% 100%, 100% 100%',
    };

    const woodDarkSquareStyle = {
        background: `
            linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.05) 50%, transparent 100%),
            linear-gradient(90deg,
                rgba(0,0,0,0.15) 0%,
                transparent 15%,
                rgba(0,0,0,0.1) 35%,
                transparent 55%,
                rgba(0,0,0,0.12) 75%,
                transparent 100%
            ),
            linear-gradient(180deg, rgba(255,255,255,0.05) 0%, transparent 50%, rgba(0,0,0,0.08) 100%),
            #b58863
        `,
        backgroundSize: '100% 100%, 6px 100%, 100% 100%, 100% 100%',
    };

    // Initialize tournament session on mount
    // NOTE: User ID is now synced globally via AuthReduxSync, not here
    useEffect(() => {
        const routeTournamentId = sessionID ? Number(sessionID) : 0;
        const requestedTournamentId = routeTournamentId || tournamentData.tournamentId || 0;

        // Entering a tournament lobby must always reset stale prior game state,
        // including when rejoining the same tournament ID after a completed game.
        const enteringLobbyRoute = !gameID;
        if (requestedTournamentId > 0 && (stateTournamentId !== requestedTournamentId || enteringLobbyRoute)) {
            dispatch(tournamentJoined(requestedTournamentId));
        }
    }, [sessionID, gameID, tournamentData.tournamentId, stateTournamentId, dispatch]);

    // URL-driven rehydration: if user refreshes on /chess/:sessionID/:gameID,
    // restore current game metadata so UI remains in-game instead of lobby states.
    useEffect(() => {
        const routeTournamentId = sessionID ? Number(sessionID) : 0;
        const routeGameId = gameID ? Number(gameID) : 0;
        if (!routeTournamentId || !routeGameId) {
            return;
        }
        if (gameData?.gameId === routeGameId && gameData?.tournamentId === routeTournamentId) {
            return;
        }

        let cancelled = false;
        const rehydrate = async () => {
            setIsRehydratingRouteGame(true);
            try {
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/game/context?tournamentId=${routeTournamentId}&gameId=${routeGameId}`);
                if (!res.ok) {
                    return;
                }
                const payload = await res.json();
                if (!cancelled && payload?.gameId === routeGameId) {
                    dispatch(gameStarting(payload));
                }
            } finally {
                if (!cancelled) {
                    setIsRehydratingRouteGame(false);
                }
            }
        };

        rehydrate();
        return () => {
            cancelled = true;
        };
    }, [sessionID, gameID, gameData, dispatch]);

    // Auto-clear bye message after 5s (UI only)
    useEffect(() => {
        if (byeMessage) {
            const timer = setTimeout(() => {
                dispatch(clearByeMessage());
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [byeMessage, dispatch]);

    // Initialize time from game data (UI only)
    useEffect(() => {
        if (gameData) {
            setBlackTime(gameData.baseTime);
            setWhiteTime(gameData.baseTime);
        }
    }, [gameData]);

    return (
        <div className="page-grid with-header">
            {/* STATE MACHINE: Show appropriate message based on state */}
            <div className="contentGrid">
                <ShellSideContainer
                    user={user}
                    gameState={gameState}
                />
                <div className="inSessionMainContent">
                    {/* STATE MACHINE: Render based on tournament state */}
                    {/* Event-driven: State machine determines if game board should be visible */}
                    {shouldShowGameBoard && gameData ? (
                        <ChessGame
                            key={gameData.gameId}
                            user={user}
                            gameId={gameData.gameId}
                            whiteId={gameData.whiteId}
                            blackId={gameData.blackId}
                            tournamentId={gameData.tournamentId}
                            whiteUsername={gameData.whiteUsername}
                            blackUsername={gameData.blackUsername}
                            startTime={whiteTime ?? 0}
                            isMuted={isMuted}
                        />
                    ) : (
                        <div>
                            <div className="gameStateIndicator">
                                {isInLobby && (
                                    <div>
                                        Lobby
                                    </div>
                                )}
                                {isWaitingForRound && (
                                    <div>
                                        {gameID && isRehydratingRouteGame ? 'Reconnecting to game...' : 'Next round starting soon...'}
                                    </div>
                                )}
                                {isTournamentOver && (
                                    <div>
                                        Tournament completed!
                                    </div>
                                )}
                                {isTournamentCancelled && (
                                    <div>
                                        Tournament has been cancelled
                                    </div>
                                )}
                            </div>
                            <div style={{ borderRadius: '4px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)' }}>
                                <DummyChessboard
                                    position={game.fen()}
                                    arePiecesDraggable={false}
                                    boardWidth={tournamentData.tournamentId != null ? 650 : 670}
                                    customSquareStyles={squareStyles}
                                    customLightSquareStyle={woodLightSquareStyle}
                                    customDarkSquareStyle={woodDarkSquareStyle}
                                    customPieces={pieceComponents}
                                />
                            </div>
                            {/* STATE MACHINE: Show bye message when in BYE_ROUND state */}
                            {isByeRound && byeMessage && (
                                <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, color: 'red', display: 'flex', justifyContent: 'center', alignItems: 'center', background: 'white', opacity: '0.3' }}>
                                    <div style={{ padding: '12px', background: 'white', opacity: '1' }}>
                                        You have a bye, you've been rewarded 0.5 points!
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {/* Game Over Modal */}
            {isGameOver && gameOverData && (
                <ResultModal
                    isOpen={true}
                    title={
                        gameOverData.outcome === '1-0'
                            ? `${gameData?.whiteUsername || 'White'} Wins!`
                            : gameOverData.outcome === '0-1'
                                ? `${gameData?.blackUsername || 'Black'} Wins!`
                                : 'Draw!'
                    }
                    metaText={`by ${gameOverData.method}`}
                    primaryLabel="Continue"
                    onPrimary={() => dispatch(clearGameOverData())}
                />
            )}
            {/* Tournament Over Modal */}
            {isTournamentOver && (
                <ResultModal
                    isOpen={true}
                    title="Tournament Over!"
                    subtitle={tournamentOverData?.winnerUsername
                        ? `${tournamentOverData.winnerUsername} wins the tournament!`
                        : "The tournament has concluded."}
                    primaryLabel="View Results"
                    onPrimary={() => window.location.href = '/chess'}
                    showHomeLink={true}
                />
            )}
        </div>
    );
}

export default ChessSessionRedux;
